import PropTypes from "prop-types";
import StrategyOverlay from "./StrategyOverlay";
import { signalValueLabel } from "../utils/signalLog";

function SignalLogPanel({ status = "empty", entries = [] }) {
    if (status === "unavailable") {
        return <div className="text-sm text-amber-200" role="status">Signal log unavailable</div>;
    }
    if (status === "malformed") {
        return <div className="text-sm text-amber-200" role="status">Malformed signal log response</div>;
    }
    if (!entries || entries.length === 0) {
        return <div className="text-sm text-gray-400" role="status">No signals found</div>;
    }

    return (
        <div>
            <h4 className="font-semibold">Latest signal</h4>
            <ul className="space-y-2">
                {entries.map((signal, i) => (
                    <li key={`${signal.timestamp?.label || "unknown"}-${signal.coin || i}-${i}`} className="rounded border border-gray-800 bg-gray-950/40 p-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold">{signal.coin || "Unknown coin"}</span>
                            <span className="rounded bg-gray-800 px-2 py-0.5 text-xs">{signal.state || "unknown"}</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-300">Time (UTC): {signal.timestamp?.label || "unknown"}</div>
                        <div className={`text-xs ${signal.timestamp?.stale ? "text-amber-200" : "text-emerald-300"}`}>
                            {signal.timestamp?.stale ? "Stale signal" : "Latest signal"}
                        </div>
                        <div className="mt-1 grid grid-cols-1 gap-1 text-xs text-gray-300 sm:grid-cols-2">
                            <span>Score: {signal.score?.score === null ? "Signal score unavailable" : signalValueLabel(signal.score?.score)}</span>
                            <span>Confidence: {signalValueLabel(signal.score?.confidence)}</span>
                            <span>Strength: {signalValueLabel(signal.score?.strength)}</span>
                            <span>Engine/module: {signal.engine || "unknown"}</span>
                            <span>Mode: {signal.mode || "unknown"}</span>
                        </div>
                        {signal.reason && <div className="mt-1 text-xs text-gray-400">Reason: {signal.reason}</div>}
                    </li>
                ))}
            </ul>
        </div>
    );
}

SignalLogPanel.propTypes = {
    status: PropTypes.oneOf(["ready", "empty", "unavailable", "malformed"]),
    entries: PropTypes.arrayOf(PropTypes.object),
};


export default function CoinDetailsModal({ coin = null, onClose }) {
    return (
        <StrategyOverlay
            open={Boolean(coin)}
            onClose={onClose}
            title={coin?.slug?.toUpperCase() || ""}
        >
            {!coin ? null : (
                <div className="text-sm space-y-3 max-h-72 overflow-y-auto">
                    <div>ROI: {coin.roi ?? "N/A"}%</div>
                    <SignalLogPanel status={coin.signalLogStatus} entries={coin.signalLog} />
                    {coin.tradeLog && coin.tradeLog.length > 0 && (
                        <div>
                            <h4 className="font-semibold">Trades</h4>
                            <ul className="list-disc ml-4">
                                {coin.tradeLog.map((t, i) => (
                                    <li key={i}>
                                        {t.timestamp}: {t.roi}%
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </StrategyOverlay>
    );
}

CoinDetailsModal.propTypes = {
    coin: PropTypes.shape({
        slug: PropTypes.string,
        roi: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        signalLogStatus: PropTypes.oneOf(["ready", "empty", "unavailable", "malformed"]),
        signalLog: PropTypes.arrayOf(PropTypes.object),
        tradeLog: PropTypes.arrayOf(
            PropTypes.shape({
                timestamp: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                ]),
                roi: PropTypes.oneOfType([
                    PropTypes.number,
                    PropTypes.string,
                ]),
            }),
        ),
    }),
    onClose: PropTypes.func.isRequired,
};

